import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { LoggerService } from '@dibsondance/shared-logging';

/**
 * In-memory cache for translation files to prevent duplicate HTTP requests
 * Translations are cached until page refresh
 */
@Injectable({
  providedIn: 'root'
})
export class TranslationCacheService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly logger = inject(LoggerService);
  private readonly cache = new Map<string, Record<string, string>>();
  
  /**
   * Get cached translation or null if not found
   */
  getTranslation(cacheKey: string): Record<string, string> | null {
    // Only use cache in browser environment
    if (!isPlatformBrowser(this.platformId)) {
      this.logger.debug(`SSR: Cache skipped for: ${cacheKey} (server-side)`, undefined, 'TranslationCacheService');
      return null;
    }
    
    const cached = this.cache.get(cacheKey);
    if (cached) {
      this.logger.debug(`Using cached translation for: ${cacheKey}`, undefined, 'TranslationCacheService');
    } else {
      this.logger.debug(`Cache miss for: ${cacheKey} (cache size: ${this.cache.size})`, undefined, 'TranslationCacheService');
    }
    return cached || null;
  }
  
  /**
   * Store translation in cache
   */
  setTranslation(cacheKey: string, translations: Record<string, string>): void {
    // Only cache in browser environment
    if (!isPlatformBrowser(this.platformId)) {
      this.logger.debug(`SSR: Cache storage skipped for: ${cacheKey} (server-side)`, undefined, 'TranslationCacheService');
      return;
    }
    
    this.logger.debug(`Caching translation for: ${cacheKey} (cache will have ${this.cache.size + 1} items)`, undefined, 'TranslationCacheService');
    this.cache.set(cacheKey, translations);
  }
  
  /**
   * Check if translation exists in cache
   */
  hasTranslation(cacheKey: string): boolean {
    return isPlatformBrowser(this.platformId) && this.cache.has(cacheKey);
  }
  
  /**
   * Clear specific translation from cache
   */
  clearTranslation(cacheKey: string): void {
    this.cache.delete(cacheKey);
  }
  
  /**
   * Clear all cached translations
   */
  clearAll(): void {
    this.logger.info('Clearing all cached translations', undefined, 'TranslationCacheService');
    this.cache.clear();
  }
  
  /**
   * Get cache statistics for debugging
   */
  getCacheStats(): { size: number, keys: string[] } {
    return {
      size: this.cache.size,
      keys: Array.from(this.cache.keys())
    };
  }
}